import { FC, useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, DatePicker, App, Tabs } from "antd";
import { ReimburseCategoryNode } from "@/utils/reimburseCategory";
import { useTranslation } from "react-i18next";
import { addReimburseItem, updateReimburseItem } from "@/apis/reimburse";
import { AddReimburseRequestItem, RequestReimbursementItem } from "@/types/reimburse";
import { ReimburseTreeSelect } from "@/components/Selector";
import SDoc from "@/components/SDoc";
import dayjs from "dayjs";

interface AddOrEditReimburseItemProps {
  open: boolean;
  applicationId: string;
  editingItem: RequestReimbursementItem | null;
  treeData: ReimburseCategoryNode[];
  onSuccess: (item: RequestReimbursementItem) => void;
  onClose: () => void;
}

const AddOrEditReimburseItem: FC<AddOrEditReimburseItemProps> = ({
  open,
  applicationId,
  editingItem,
  treeData,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [docId, setDocId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"info" | "attachment">("info");

  useEffect(() => {
    if (open) {
      if (editingItem) {
        form.setFieldsValue({
          categoryId: editingItem.categoryId,
          amount: editingItem.amount,
          expenseDate: editingItem.expenseDate ? dayjs(editingItem.expenseDate) : null,
          description: editingItem.description || "",
          invoiceCount: editingItem.invoiceCount,
          remark: editingItem.remark || "",
        });
        setDocId(editingItem.docId || "");
      } else {
        form.setFieldsValue({
          expenseDate: dayjs(),
        });
        setDocId("");
      }
      setActiveTab("info");
    }
  }, [open, editingItem, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const params: AddReimburseRequestItem = {
        categoryId: values.categoryId,
        amount: values.amount,
        expenseDate: values.expenseDate ? values.expenseDate.valueOf() : Date.now(),
        description: values.description || "",
        invoiceCount: values.invoiceCount || 0,
        remark: values.remark || "",
        docId,
      };

      let result: RequestReimbursementItem;
      if (editingItem) {
        result = await updateReimburseItem(editingItem.id, params);
        notification.success({ placement: 'top',
          message: t("common.tip"),
          description: t("common.updateSuccess"),
        });
      } else {
        result = await addReimburseItem(applicationId, params);
        notification.success({ placement: 'top',
          message: t("common.tip"),
          description: t("common.addSuccess"),
        });
      }

      onSuccess(result);
      handleClose();
    } catch (error: unknown) {
      console.error("Form submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setDocId("");
    onClose();
  };

  const tabItems = [
    {
      key: "info",
      label: t("common.basicInfo"),
      children: (
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label={t("reimburse.reimbursement")}
            name="categoryId"
            rules={[{ required: true, message: t("layout.reimburseCategory") }]}
          >
            <ReimburseTreeSelect treeData={treeData} />
          </Form.Item>

          <Form.Item
            label={t("reimburse.amount")}
            name="amount"
            rules={[{ required: true, message: t("reimburse.plAmount") }]}
          >
            <InputNumber changeOnWheel
              min={0}
              precision={2}
              style={{ width: "100%" }}
              placeholder={t("reimburse.amount")}
              formatter={(value: string | number | undefined) => value ? `${Number(value).toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
              parser={(value: string | undefined) => value ? parseFloat(value.replace(/,/g, '')) : 0}
            />
          </Form.Item>

          <Form.Item
            label={t("reimburse.invoiceCount")}
            name="invoiceCount"
          >
            <InputNumber changeOnWheel
              style={{ width: "100%" }}
              min={0}
              precision={0}
              placeholder={t("reimburse.invoiceCount")}
            />
          </Form.Item>

          <Form.Item
            label={t("reimburse.expenseDate")}
            name="expenseDate"
            rules={[{ required: true, message: t("reimburse.plExpenseDate") }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item
            label={t("common.desc")}
            name="description"
            rules={[{ required: true, message: t("components.plDictDesc") }]}
          >
            <Input.TextArea
              rows={3}
              placeholder={t("common.desc")}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={t("reimburse.remark")}
            name="remark"
          >
            <Input.TextArea
              rows={2}
              placeholder={t("reimburse.remark")}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "attachment",
      label: t("common.attachment"),
      children: (
        <SDoc
          docId={docId}
          handleNewDocId={(id) => {
            if (!editingItem) {
              setDocId(id);
            }
          }}
        />
      ),
    },
  ];

  return (
    <Modal
      title={editingItem ? t("common.edit") : t("common.add")}
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      destroyOnClose
      width={700}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as "info" | "attachment")}
        items={tabItems}
      />
    </Modal>
  );
};

export default AddOrEditReimburseItem;
